import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useRouter } from 'expo-router';
import {
  MenuNavigationTabBorderBottom,
  MenuNavigationTabLink,
  MenuNavigationTabText,
} from 'features/menu/components/menu-navigation-tabs/styled';
import { LayoutChangeEvent, Platform, ScrollView } from 'react-native';

import { DraggableScrollView } from 'components/draggable-scrollview';
import { CenteredRowContainer } from 'components/layout/centered-row-container';
import { useRoute } from 'hooks/navigation/use-route';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import {
  ClickEventComponentNames,
  CustomEventNames,
  EventTypes,
  logButtonClick,
  logEvent,
} from 'state/amplitude';
import { IMainMenuNode } from 'state/unified-menu/types';

interface IMenuNavigationTabsProps {
  mainMenu: IMainMenuNode[];
  headerBackgroundColorOverride?: string | null;
}

export const MenuNavigationTabs: React.FC<IMenuNavigationTabsProps> = ({
  mainMenu,
  headerBackgroundColorOverride,
}) => {
  const [coordinates, setCoordinates] = useState<{ [key: number]: number } | null>(null);
  const scrollViewRef = useRef<ScrollView | null>(null);
  const { params } = useRoute<{ id: string }>();
  const router = useRouter();

  const { id = '' } = params || {};
  const isDesktop = useIsDesktopBreakpoint();
  const isDesktopWeb = isDesktop && Platform.OS === 'web';
  const ScrollViewComponent = isDesktopWeb ? DraggableScrollView : ScrollView;

  const activeTabIndex = useMemo(() => {
    return mainMenu.findIndex(item => item.id === id);
  }, [mainMenu, id]);

  const onItemLayout = useCallback((event: LayoutChangeEvent, index: number) => {
    const { x } = event.nativeEvent.layout;
    setCoordinates(prev => ({ ...prev, [index]: x }));
  }, []);

  useLayoutEffect(() => {
    if (coordinates && coordinates[activeTabIndex] !== undefined) {
      scrollViewRef.current?.scrollTo({ x: coordinates[activeTabIndex], animated: true });
    }
  }, [activeTabIndex, coordinates]);

  const handleOnPress = useCallback(
    (section: IMainMenuNode) => {
      logEvent(CustomEventNames.TABBED_HEADER, EventTypes.Navigation, {
        'Selected Tab': section.name,
      });

      logButtonClick({
        attributes: {
          component: ClickEventComponentNames.PRODUCT_SECTION_TABS,
          componentId: section.id,
          name: section.name,
        },
      });
      // Moved linking to avoid conflict with the draggable scrollview component.
      router.setParams({ id: section.id });
    },
    [router]
  );

  return (
    <CenteredRowContainer
      testID="tabbed-menu-header"
      style={{ backgroundColor: headerBackgroundColorOverride || undefined }}
    >
      <ScrollViewComponent ref={scrollViewRef} horizontal showsHorizontalScrollIndicator={false}>
        {mainMenu.map((section, index) => (
          <MenuNavigationTabLink
            key={index}
            testID={`tabbed-menu-${section.name}`}
            active={index === activeTabIndex}
            onPress={() => handleOnPress(section)}
            onLayout={event => onItemLayout(event, index)}
          >
            <MenuNavigationTabText active={index === activeTabIndex}>
              {section.name}
            </MenuNavigationTabText>
          </MenuNavigationTabLink>
        ))}
      </ScrollViewComponent>
      {isDesktopWeb && <MenuNavigationTabBorderBottom />}
    </CenteredRowContainer>
  );
};
